import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Sparkles, RefreshCw, AlertCircle, CheckCircle2, Zap, Database, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { GlassCard } from '../shared';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';

/**
 * Admin card to trigger lexical-gloss generation (lemmes/racines → FR/EN
 * via OpenAI / Anthropic / Emergent universal key, picked automatically).
 *
 * The job runs as a background task on the backend; this component polls
 * `/api/admin/lexical/status` every 4 s to surface progress.
 */
const LexicalImportCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [confirmSync, setConfirmSync] = useState(false);

  const [type, setType] = useState('all'); // lemma | root | all
  const [language, setLanguage] = useState('fr');
  const [limit, setLimit] = useState(0);
  const [overwrite, setOverwrite] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await adminApi.lexical.status();
      setStatus(r.data);
    } catch (e) {
      console.error('lexical status', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 4000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminApi.lexical.generate({
        languages: language,
        type,
        limit: Number(limit) || 0,
        overwrite,
      });
      toast.success('Génération lancée en arrière-plan');
      await fetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Erreur : ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFullSync = async () => {
    setSyncing(true);
    try {
      await adminApi.lexical.fullSync();
      toast.success('Synchronisation lancée — suivez la progression ci-dessous');
      await fetchStatus();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Erreur : ${msg}`);
    } finally {
      setSyncing(false);
      setConfirmSync(false);
    }
  };

  const job = status?.job;
  const sync = status?.sync;
  const counts = status?.counts || {};
  const provider = status?.provider || 'unknown';
  const isRunning = !!job?.running;
  const isSyncRunning = !!sync?.running;

  return (
    <GlassCard data-testid="admin-lexical-import-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-serif text-lg text-text-primary flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Import lexical (définitions IA)
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Génère les sens (lemmes &amp; racines) en français via{' '}
            <span className="font-mono text-primary">{provider}</span>. Idempotent — ne
            retraduit pas les entrées déjà glosées sauf si « réécrire » est coché.
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={fetchStatus}
          disabled={loading}
          data-testid="admin-lexical-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        {[
          { k: 'lemmas', label: 'Lemmes' },
          { k: 'roots', label: 'Racines' },
          { k: 'glosses', label: 'Gloses (total)' },
          { k: 'glosses_fr_lemma', label: 'FR / lemmes' },
          { k: 'glosses_fr_root', label: 'FR / racines' },
        ].map((c) => (
          <div
            key={c.k}
            className="rounded-lg border border-border bg-surface-elevated/50 p-2 text-center"
            data-testid={`admin-lexical-count-${c.k}`}
          >
            <div className="text-lg font-serif text-primary">
              {loading ? '…' : (counts[c.k] ?? 0)}
            </div>
            <div className="text-xs text-text-muted">{c.label}</div>
          </div>
        ))}
      </div>

      {/* Full sync from local data sources (morphology + lemma/root + glossary) */}
      <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 p-3"
           data-testid="admin-lexical-full-sync-block">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-text-primary font-medium">
              <Database className="w-4 h-4 text-primary" />
              Synchroniser le lexique depuis les sources
            </div>
            <p className="text-xs text-text-muted mt-1">
              Réimporte la morphologie QAC, reconstruit lemmes/racines et charge le
              glossaire bundle. À utiliser sur une production fraîche pour atteindre
              la parité avec l'environnement de dev. Idempotent.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setConfirmSync(true)}
            disabled={isSyncRunning || isRunning}
            className="bg-primary text-background"
            data-testid="admin-lexical-full-sync-btn"
          >
            {isSyncRunning ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sync en cours…</>
            ) : (
              <><Database className="w-4 h-4 mr-1" />Lancer la synchronisation</>
            )}
          </Button>
        </div>

        {/* Sync state / step report */}
        {sync && (sync.started_at || (sync.steps && sync.steps.length > 0)) && (
          <div className="mt-3 text-xs space-y-1" data-testid="admin-lexical-sync-state">
            {sync.last_message && (
              <div className="text-text-secondary">{sync.last_message}</div>
            )}
            {(sync.steps || []).map((s, i) => (
              <div key={i} className="flex items-center gap-2 text-text-secondary">
                <ChevronRight className="w-3 h-3 text-text-muted" />
                <span className="font-mono text-text-muted">{s.name}</span>
                <span className={
                  s.status === 'ok' ? 'text-emerald-400'
                  : s.status === 'skipped' ? 'text-amber-400'
                  : 'text-rose-400'
                }>{s.status}</span>
                {s.detail && <span className="text-text-muted truncate">— {s.detail}</span>}
              </div>
            ))}
            {sync.last_error && (
              <div className="text-rose-300 font-mono text-[10px] mt-1">{sync.last_error}</div>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={confirmSync} onOpenChange={setConfirmSync}>
        <AlertDialogContent data-testid="admin-lexical-full-sync-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Lancer la synchronisation du lexique ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'opération peut durer plusieurs minutes (parsing QAC, reconstruction
              des lemmes/racines, import du glossaire). Elle est idempotente, mais
              vous ne pourrez pas l'interrompre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={syncing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFullSync}
              disabled={syncing}
              className="bg-primary text-background"
              data-testid="admin-lexical-full-sync-confirm-btn"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Database className="w-4 h-4 mr-2" />}
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Form */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-xs text-text-secondary">Type</Label>
          <Select value={type} onValueChange={setType} disabled={isRunning}>
            <SelectTrigger data-testid="admin-lexical-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous (lemmes + racines)</SelectItem>
              <SelectItem value="lemma">Lemmes seulement</SelectItem>
              <SelectItem value="root">Racines seulement</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-text-secondary">Langue</Label>
          <Select value={language} onValueChange={setLanguage} disabled={isRunning}>
            <SelectTrigger data-testid="admin-lexical-lang-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr,en">FR + EN</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-text-secondary" htmlFor="lex-limit">Limite (0 = tout)</Label>
          <Input
            id="lex-limit"
            type="number"
            min={0}
            max={10000}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            disabled={isRunning}
            data-testid="admin-lexical-limit-input"
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input
              type="checkbox"
              checked={overwrite}
              onChange={(e) => setOverwrite(e.target.checked)}
              disabled={isRunning}
              data-testid="admin-lexical-overwrite-checkbox"
            />
            Réécrire
          </label>
          <Button
            type="submit"
            disabled={submitting || isRunning}
            className="bg-primary text-background"
            data-testid="admin-lexical-start-btn"
          >
            {submitting || isRunning ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" />En cours…</>
            ) : (
              <><Zap className="w-4 h-4 mr-1" />Lancer</>
            )}
          </Button>
        </div>
      </form>

      {/* Job state */}
      {job && (job.started_at || job.last_message) && (
        <div
          className="mt-4 p-3 rounded-lg border border-border bg-surface-elevated/40 text-sm"
          data-testid="admin-lexical-job-state"
        >
          <div className="flex items-center gap-2 mb-1">
            {isRunning && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            {!isRunning && job.last_error && <AlertCircle className="w-4 h-4 text-rose-400" />}
            {!isRunning && !job.last_error && job.finished_at && (
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
            )}
            <span className="text-text-primary">
              {isRunning ? 'Tâche en cours' : (job.last_error ? 'Échec' : 'Dernière tâche')}
            </span>
          </div>
          {job.last_message && (
            <div className="text-text-secondary">{job.last_message}</div>
          )}
          {job.last_error && (
            <div className="text-rose-300 font-mono text-xs mt-1">{job.last_error}</div>
          )}
          <div className="text-text-muted text-xs mt-1">
            Lots : {job.totals?.batches ?? 0} ·
            Insérés : {job.totals?.inserted ?? 0} ·
            Lots vides : {job.totals?.skipped_batches ?? 0}
            {job.started_at && <> · Démarré : {new Date(job.started_at).toLocaleString()}</>}
          </div>
        </div>
      )}
    </GlassCard>
  );
};

export default LexicalImportCard;
